import { useState } from "react";
import {
  useGetAlphaBscWalletQuery,
  useGetAlphaBscTokensQuery,
  useGetAlphaBscPositionsQuery,
  useAlphaBscBuyMutation,
  useAlphaBscSellMutation,
  useRefreshAlphaBscLiveMutation,
} from "@/store/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Table, THead, TBody, TR, TH, TD } from "@/components/ui/table";
import { Skeleton } from "@/components/ui/skeleton";
import { fmtPrice, fmtQty } from "@/lib/format";
import { TokenSparkline } from "@/components/domain/TokenSparkline";
import { toast } from "sonner";

/**
 * AlphaBscTradingCard — Phase 2 on-chain trading on PancakeSwap V3.
 *
 * Three sections:
 *   1. Wallet status (address, BNB, USDT, BscScan link)
 *   2. Tracked tokens with inline BUY buttons
 *   3. Open + closed positions with SELL buttons
 *
 * Wallet must be configured before any trading is possible (run
 * `python -m bot.web3_layer.bsc_wallet generate` then add to .env).
 * Master switch `bsc_trading_enabled` must be true.
 */
export function AlphaBscTradingCard() {
  const wallet = useGetAlphaBscWalletQuery(undefined, { pollingInterval: 30000 });
  const tokens = useGetAlphaBscTokensQuery("watching", { pollingInterval: 60000 });
  const allOpen = useGetAlphaBscPositionsQuery("open", { pollingInterval: 30000 });
  const closedAll = useGetAlphaBscPositionsQuery("closed", { pollingInterval: 120000 });
  // Manual tab shows ONLY manual positions (auto positions live on Auto-strategy tab)
  const openPos = { ...allOpen, data: (allOpen.data || []).filter((p: any) => !p.auto_managed) };
  const closedPos = { ...closedAll, data: (closedAll.data || []).filter((p: any) => !p.auto_managed) };
  const [buyMut, { isLoading: buying }] = useAlphaBscBuyMutation();
  const [sellMut, { isLoading: selling }] = useAlphaBscSellMutation();
  const [refreshLive, { isLoading: refreshing }] = useRefreshAlphaBscLiveMutation();
  const [sizeByToken, setSizeByToken] = useState<Record<number, string>>({});

  async function onRefreshLive() {
    try {
      const r: any = await refreshLive().unwrap();
      const grads = r?.graduations_detected?.length ?? 0;
      toast.success(
        grads > 0
          ? `🎓 ${grads} graduation(s)! refreshed ${r?.refreshed} tokens`
          : `refreshed ${r?.refreshed}/${r?.total_watched} via DexScreener`,
      );
      allOpen.refetch();
      closedAll.refetch();
      tokens.refetch();
      wallet.refetch();
    } catch (e: any) {
      toast.error(`refresh failed: ${e?.data?.detail ?? "?"}`);
    }
  }

  async function onBuy(tokenId: number, symbol: string) {
    const sizeRaw = sizeByToken[tokenId] ?? "25";
    const size = Number(sizeRaw);
    if (!Number.isFinite(size) || size <= 0) {
      toast.error("Enter a valid USDT size");
      return;
    }
    if (!confirm(`Buy $${size} of ${symbol} via PancakeSwap V3 (real money)?`)) return;
    try {
      const r: any = await buyMut({ token_id: tokenId, size_usdt: size }).unwrap();
      if (r.status === "ok") {
        toast.success(
          `BOUGHT ${r.symbol} — ${fmtQty(r.qty_token)} @ ${fmtPrice(r.entry_price_usd)} | tx ${r.tx_hash.slice(0,10)}…`,
          { duration: 10000 }
        );
      } else {
        toast.error(`buy failed: ${r.reason}`);
      }
    } catch (e: any) {
      toast.error(`buy error: ${e?.data?.detail ?? e?.message ?? "?"}`);
    }
  }

  async function onSell(posId: number, symbol: string, pct: number) {
    if (!confirm(`Sell ${(pct * 100).toFixed(0)}% of ${symbol} position?`)) return;
    try {
      const r: any = await sellMut({ position_id: posId, pct_to_sell: pct }).unwrap();
      if (r.status === "ok") {
        toast.success(
          `SOLD ${r.symbol} — proceeds $${r.proceeds_usdt.toFixed(4)} | P&L ${r.realized_pnl_usd >= 0 ? "+" : ""}$${r.realized_pnl_usd.toFixed(4)}`,
          { duration: 10000 }
        );
      } else {
        toast.error(`sell failed: ${r.reason}`);
      }
    } catch (e: any) {
      toast.error(`sell error: ${e?.data?.detail ?? e?.message ?? "?"}`);
    }
  }

  const w = wallet.data || {};
  const configured = w.configured === true;

  return (
    <Card>
      <CardHeader className="flex-row items-start justify-between gap-2 flex-wrap">
        <CardTitle className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
          <span>Alpha BSC Trading</span>
          <Badge tone="warn">Phase 2 — REAL MONEY on BSC</Badge>
          <span className="text-xs text-muted font-normal">
            PancakeSwap V3 · 2% slippage · $25/trade default · daily $100 cap
          </span>
        </CardTitle>
        <Button size="sm" variant="ghost" onClick={onRefreshLive} disabled={refreshing}>
          {refreshing ? "Refreshing…" : "↻ Refresh live"}
        </Button>
      </CardHeader>
      <CardContent className="space-y-4">

        {/* WALLET STATUS */}
        <div className="rounded border p-3 bg-muted/10">
          <div className="font-semibold mb-2">BSC Wallet</div>
          {wallet.isLoading && <Skeleton className="h-12 w-full" />}
          {!configured && (
            <div className="text-sm text-yellow-500">
              ⚠ Wallet not configured. SSH into the box and run:
              <pre className="mt-2 text-xs bg-bg text-fg/50 p-2 rounded font-mono">
{`cd /home/ec2-user/trading-ai-multi
/home/ec2-user/trading-ai/.venv/bin/python -m bot.web3_layer.bsc_wallet generate
# Add the printed BSC_WALLET_ADDRESS and BSC_WALLET_PRIVATE_KEY to .env
# Then: sudo systemctl restart trading-ai-multi.service`}
              </pre>
            </div>
          )}
          {configured && (
            <div className="grid grid-cols-2 gap-2 text-xs">
              <div>
                <div className="text-muted">Address</div>
                <a href={w.bscscan_url} target="_blank" rel="noopener noreferrer"
                   className="font-mono hover:underline">
                  {w.address.slice(0,8)}…{w.address.slice(-6)} ↗
                </a>
              </div>
              <div>
                <div className="text-muted">RPC</div>
                <span>{w.rpc_connected ? `✓ chain ${w.chain_id} block ${w.latest_block?.toLocaleString()}` : "✗ offline"}</span>
              </div>
              <div>
                <div className="text-muted">BNB (gas)</div>
                <strong className={w.bnb_balance < 0.005 ? "text-red-500" : ""}>
                  {Number(w.bnb_balance ?? 0).toFixed(6)}
                </strong>
                {w.bnb_balance < 0.005 && (
                  <span className="text-red-500 ml-2">⚠ low — fund $5 BNB</span>
                )}
              </div>
              <div>
                <div className="text-muted">USDT (BEP-20)</div>
                <strong>${Number(w.usdt_balance?.formatted ?? 0).toFixed(4)}</strong>
              </div>
            </div>
          )}
        </div>

        {/* TRACKED TOKENS — BUY ACTIONS */}
        {configured && (
          <div>
            <div className="font-semibold mb-2">Buy from watchlist</div>
            {(!tokens.data || tokens.data.length === 0) && (
              <div className="text-xs text-muted italic">No tokens in watchlist. Add some via "Alpha BSC Watch" above.</div>
            )}
            {tokens.data && tokens.data.length > 0 && (
              <Table>
                <THead>
                  <TR>
                    <TH>Symbol</TH>
                    <TH className="text-right" title="Graduation likelihood (research + news + volume + trading, 0-100)">
                      Likelihood
                    </TH>
                    <TH title="Latest Gemini deep research verdict — hover for thesis">
                      Research
                    </TH>
                    <TH className="text-right" title="Trading-pattern score only">
                      Pattern
                    </TH>
                    <TH className="text-right">Price USD</TH>
                    <TH className="text-right">24h vol</TH>
                    <TH className="text-right">Liquidity</TH>
                    <TH className="text-center">Action</TH>
                  </TR>
                </THead>
                <TBody>
                  {tokens.data.map((t: any) => {
                    const lik = Number(t.graduation_likelihood_score ?? 0);
                    const likTone = lik >= 70 ? "good" : lik >= 40 ? "warn" : "muted";
                    return (
                    <TR key={t.id}>
                      <TD className="font-mono font-semibold">{t.symbol}</TD>
                      <TD className="text-right"
                          title={t.graduation_likelihood_summary || "Not ranked yet"}>
                        {lik > 0 ? (
                          <Badge tone={likTone as any}>🎓 {lik.toFixed(0)}</Badge>
                        ) : (
                          <span className="text-muted text-xs">—</span>
                        )}
                      </TD>
                      <TD title={t.latest_research_thesis || "No deep research yet"}>
                        {t.latest_research_verdict ? (
                          <Badge tone={
                            t.latest_research_verdict === "GO" ? "good"
                            : t.latest_research_verdict === "WAIT" ? "warn"
                            : t.latest_research_verdict === "SKIP" ? "bad"
                            : "muted"
                          }>
                            {t.latest_research_verdict} {t.latest_research_confidence}%
                          </Badge>
                        ) : (
                          <span className="text-muted text-xs">—</span>
                        )}
                      </TD>
                      <TD className="text-right">
                        <Badge tone={t.graduation_score >= 60 ? "good" : t.graduation_score >= 40 ? "warn" : "muted"}>
                          {Number(t.graduation_score).toFixed(0)}
                        </Badge>
                      </TD>
                      <TD className="text-right tabular-nums font-mono text-xs">
                        {fmtPrice(Number(t.price_usd ?? 0))}
                      </TD>
                      <TD className="text-right tabular-nums">
                        ${Number(t.volume_24h_usd ?? 0).toLocaleString(undefined, {maximumFractionDigits: 0})}
                      </TD>
                      <TD className="text-right tabular-nums">
                        ${Number(t.liquidity_usd ?? 0).toLocaleString(undefined, {maximumFractionDigits: 0})}
                      </TD>
                      <TD className="text-center">
                        <div className="flex items-center justify-center gap-1">
                          <input type="number" min="5" max="100" step="5"
                                 value={sizeByToken[t.id] ?? "25"}
                                 onChange={(e) => setSizeByToken({...sizeByToken, [t.id]: e.target.value})}
                                 className="w-16 px-1 py-0.5 text-xs text-right rounded border bg-bg text-fg tabular-nums" />
                          <Button size="sm" onClick={() => onBuy(t.id, t.symbol)} disabled={buying}>
                            {buying ? "..." : "Buy"}
                          </Button>
                        </div>
                      </TD>
                    </TR>
                    );
                  })}
                </TBody>
              </Table>
            )}
          </div>
        )}

        {/* OPEN POSITIONS — aggregated by symbol (same-symbol multi-fills shown as blended avg) */}
        {configured && openPos.data && openPos.data.length > 0 && (() => {
          // Group positions by symbol
          const grouped: Record<string, any[]> = {};
          for (const p of openPos.data) {
            if (!grouped[p.symbol]) grouped[p.symbol] = [];
            grouped[p.symbol].push(p);
          }
          const aggregates = Object.entries(grouped).map(([sym, positions]: [string, any[]]) => {
            const total_size = positions.reduce((s, p) => s + Number(p.entry_size_usdt || 0), 0);
            const total_qty = positions.reduce((s, p) => s + Number(p.entry_qty_token || 0), 0);
            const blended_avg = total_qty > 0 ? total_size / total_qty : 0;
            const cur_price = Number(positions[0].current_price_usd ?? 0);
            const cur_value = cur_price * total_qty;
            const unreal_usd = cur_value - total_size;
            const unreal_pct = total_size > 0 ? (unreal_usd / total_size) * 100 : 0;
            const has_live = cur_price > 0;
            return {
              symbol: sym,
              token_id: positions[0].token_id,
              positions, total_size, total_qty, blended_avg,
              cur_price, cur_value, unreal_usd, unreal_pct, has_live,
            };
          });
          return (
            <div>
              <div className="font-semibold mb-2">
                Open manual positions ({aggregates.length} symbols, {openPos.data.length} fills)
                <span className="ml-2 text-xs font-normal text-muted">
                  · Same-symbol fills shown as blended avg. Auto positions on
                  <span className="font-semibold"> Auto-strategy</span> tab.
                </span>
              </div>
              <Table>
                <THead>
                  <TR>
                    <TH>Symbol</TH>
                    <TH className="text-right">Fills</TH>
                    <TH className="text-right">Total spent</TH>
                    <TH className="text-right">Total qty</TH>
                    <TH className="text-right">Avg cost</TH>
                    <TH className="text-right">Cur price</TH>
                    <TH className="text-right">Unrealized</TH>
                    <TH>Chart</TH>
                    <TH className="text-center">Sell (oldest)</TH>
                  </TR>
                </THead>
                <TBody>
                  {aggregates.map((a) => (
                    <TR key={a.symbol}>
                      <TD className="font-mono font-semibold">{a.symbol}</TD>
                      <TD className="text-right tabular-nums">
                        {a.positions.length > 1
                          ? <span className="font-semibold">{a.positions.length}×</span>
                          : "1"}
                      </TD>
                      <TD className="text-right tabular-nums">${a.total_size.toFixed(2)}</TD>
                      <TD className="text-right tabular-nums font-mono text-xs">{fmtQty(a.total_qty)}</TD>
                      <TD className="text-right tabular-nums font-mono text-xs">{fmtPrice(a.blended_avg)}</TD>
                      <TD className="text-right tabular-nums font-mono text-xs">
                        {a.has_live ? fmtPrice(a.cur_price) : <span className="text-muted">—</span>}
                      </TD>
                      <TD className={`text-right tabular-nums font-semibold text-xs ${
                        a.has_live ? (a.unreal_pct >= 0 ? "text-green-500" : "text-red-500") : "text-muted"
                      }`}>
                        {a.has_live ? (
                          <div>
                            <div>{a.unreal_pct >= 0 ? "+" : ""}{a.unreal_pct.toFixed(2)}%</div>
                            <div className="text-[10px] opacity-80">{a.unreal_usd >= 0 ? "+" : ""}${a.unreal_usd.toFixed(4)}</div>
                          </div>
                        ) : "—"}
                      </TD>
                      <TD>
                        <TokenSparkline tokenId={a.token_id} entryPrice={a.blended_avg} />
                      </TD>
                      <TD className="text-center">
                        <div className="flex gap-1 justify-center">
                          <Button size="sm" variant="ghost"
                                  onClick={() => onSell(a.positions[a.positions.length - 1].id, a.symbol, 0.5)}
                                  disabled={selling}>50%</Button>
                          <Button size="sm"
                                  onClick={() => onSell(a.positions[a.positions.length - 1].id, a.symbol, 1.0)}
                                  disabled={selling}>All</Button>
                        </div>
                      </TD>
                    </TR>
                  ))}
                </TBody>
              </Table>
              {/* Expandable per-fill detail for any multi-fill group */}
              {aggregates.some((a) => a.positions.length > 1) && (
                <details className="mt-2 text-xs">
                  <summary className="cursor-pointer text-muted hover:text-fg">
                    Show individual fills ({openPos.data.length} total)
                  </summary>
                  <Table>
                    <THead>
                      <TR>
                        <TH>Symbol</TH>
                        <TH>pid</TH>
                        <TH className="text-right">Spent</TH>
                        <TH className="text-right">Qty</TH>
                        <TH className="text-right">Entry $</TH>
                        <TH>Opened</TH>
                        <TH>Tx</TH>
                      </TR>
                    </THead>
                    <TBody>
                      {openPos.data.map((p: any) => (
                        <TR key={p.id}>
                          <TD className="font-mono">{p.symbol}</TD>
                          <TD>{p.id}</TD>
                          <TD className="text-right tabular-nums">${Number(p.entry_size_usdt).toFixed(2)}</TD>
                          <TD className="text-right tabular-nums font-mono text-[10px]">{fmtQty(Number(p.entry_qty_token))}</TD>
                          <TD className="text-right tabular-nums font-mono text-[10px]">{fmtPrice(Number(p.entry_price_usd))}</TD>
                          <TD className="text-muted">{p.entry_ts?.slice(5,16)}</TD>
                          <TD>
                            <a href={`https://bscscan.com/tx/${p.entry_tx_hash}`} target="_blank" rel="noopener noreferrer"
                               className="hover:underline text-muted font-mono">
                              {p.entry_tx_hash?.slice(0,8)}…
                            </a>
                          </TD>
                        </TR>
                      ))}
                    </TBody>
                  </Table>
                </details>
              )}
            </div>
          );
        })()}

        {/* CLOSED POSITIONS — HISTORY */}
        {configured && closedPos.data && closedPos.data.length > 0 && (
          <div>
            <div className="font-semibold mb-2">Closed positions ({closedPos.data.length})</div>
            <Table>
              <THead>
                <TR>
                  <TH>Symbol</TH>
                  <TH className="text-right">Spent</TH>
                  <TH className="text-right">Proceeds</TH>
                  <TH className="text-right">P&L</TH>
                  <TH>Exit tx</TH>
                </TR>
              </THead>
              <TBody>
                {closedPos.data.slice(0, 20).map((p: any) => (
                  <TR key={p.id}>
                    <TD className="font-mono">{p.symbol}</TD>
                    <TD className="text-right tabular-nums text-xs">${Number(p.entry_size_usdt).toFixed(4)}</TD>
                    <TD className="text-right tabular-nums text-xs">${Number(p.exit_proceeds_usdt ?? 0).toFixed(4)}</TD>
                    <TD className={`text-right tabular-nums font-semibold ${(p.realized_pnl_usd ?? 0) >= 0 ? "text-green-500" : "text-red-500"}`}>
                      {(p.realized_pnl_usd ?? 0) >= 0 ? "+" : ""}${Number(p.realized_pnl_usd ?? 0).toFixed(4)}
                    </TD>
                    <TD className="text-xs">
                      <a href={`https://bscscan.com/tx/${p.exit_tx_hash}`} target="_blank" rel="noopener noreferrer"
                         className="hover:underline text-muted font-mono">
                        {p.exit_tx_hash?.slice(0,8)}…
                      </a>
                    </TD>
                  </TR>
                ))}
              </TBody>
            </Table>
          </div>
        )}

      </CardContent>
    </Card>
  );
}
